import { Immutable } from "immer";
import React from "react";

import type { AccordionSection as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

type Section = NonNullable<Value["sections"]>[number];

/**
 * One accordion disclosure, owning its own open/closed state. Clicking the
 * trigger toggles ``accordion--is-active`` on the button and
 * ``accordion-target--is-active`` on the panel (the panel is ``display:none``
 * until that class -- see ``_accordion.scss``), keeping ``aria-expanded`` in
 * sync. Sections render collapsed by default so SSR and hydration agree. The
 * native ``<button>`` activates on Enter/Space, so no explicit key handler is
 * needed.
 */
const AccordionItem = (
    props: Immutable<{ section: Section; targetId: string }>,
): React.JSX.Element => {
    const { section, targetId } = props;
    const [isActive, setIsActive] = React.useState(false);
    return (
        <li className="accordion__list-item">
            <button
                type="button"
                className={
                    isActive ? "accordion accordion--is-active" : "accordion"
                }
                aria-expanded={isActive ? "true" : "false"}
                aria-controls={targetId}
                onClick={() => {
                    setIsActive((value) => !value);
                }}
            >
                {section.header}
            </button>
            <div
                id={targetId}
                className={
                    isActive
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                <RichTextBlock value={section.body ?? ""} />
            </div>
        </li>
    );
};

export const AccordionSection = (props: Props): React.JSX.Element | null => {
    const v = props.value;
    const sections = v.sections ?? [];
    return (
        <div className="accordion__container">
            <h2 className="accordion__title">{v.block_header}</h2>
            <ul className="accordion__list">
                {sections.map((section, i) => (
                    <AccordionItem
                        key={i}
                        section={section}
                        targetId={`accordion-target-${i}`}
                    />
                ))}
            </ul>
        </div>
    );
};
